
import { ROLES, type Role } from '@shared/user';
import { ROLE_LABEL } from '@client/shared/design';
import { cx } from '@client/shared/lib/cx.ts';
import styles from './RolePicker.module.css';

const ROLE_DESCRIPTION: Record<Role, string> = {
  agent: 'Can book open houses',
  admin: 'Can create listings, book open houses for agents, and manage users',
  billing: 'Can manage payment details',
  owner: 'Transfer ownership in group settings',
};

export interface RolePickerProps {
  value: readonly Role[];
  onChange: (roles: Role[]) => void;
  name: string;
  disabled?: boolean;
  lockedRoles?: readonly Role[];
  /** Which roles to offer, in display order. Defaults to all roles. */
  roles?: readonly Role[];
  /** Stack roles full-width in one column instead of the auto-fit grid. */
  stacked?: boolean;
  id?: string;
  'aria-invalid'?: boolean | undefined;
  'aria-describedby'?: string | undefined;
}

export function RolePicker({
  value,
  onChange,
  name,
  disabled = false,
  lockedRoles = [],
  roles = ROLES,
  stacked = false,
  id,
  'aria-invalid': ariaInvalid,
  'aria-describedby': ariaDescribedBy,
}: RolePickerProps) {
  function toggle(role: Role) {
    if (lockedRoles.includes(role)) return;
    const has = value.includes(role);
    onChange(has ? value.filter((r) => r !== role) : [...value, role]);
  }

  return (
    <div
      className={cx(styles.group, stacked && styles.stacked)}
      role="group"
      id={id}
      aria-invalid={ariaInvalid}
      aria-describedby={ariaDescribedBy}
      data-invalid={ariaInvalid || undefined}
    >
      {roles.map((role) => {
        const locked = lockedRoles.includes(role);
        const selected = locked || value.includes(role);
        // Owner is set at org creation, not reassigned here.
        const roleDisabled = disabled || locked || role === 'owner';
        const description = ROLE_DESCRIPTION[role];
        return (
          <label
            key={role}
            className={cx(styles.chip, selected && styles.selected, locked && styles.locked)}
            data-role={role}
            data-disabled={roleDisabled || undefined}
            data-locked={locked || undefined}
          >
            <input
              type="checkbox"
              className={styles.input}
              name={name}
              value={role}
              checked={selected}
              disabled={roleDisabled}
              onChange={() => toggle(role)}
            />
            <span className={styles.box} aria-hidden="true">
              <CheckMark className={styles.check} />
            </span>
            <span className={styles.body}>
              <span className={styles.name}>
                {ROLE_LABEL[role]}
                {locked && <span className={styles.required}> · Required</span>}
              </span>
              {description && <span className={styles.desc}>{description}</span>}
            </span>
          </label>
        );
      })}
    </div>
  );
}

function CheckMark({ className }: { className?: string }) {
  return (
    <svg className={className} width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M3.5 8.5l3 3 6-6.5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
